import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Flex, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import {
  checkDownloadSpeed,
  DEFAULT_DOWNLOAD_CHECK,
} from "./util/network-download-check";
import {
  checkUploadSpeed,
  DEFAULT_UPLOAD_CHECK,
} from "./util/network-upload-check";
import "./network-check.less";
import { PprItemLayout } from "../../../shared/ppr-item-layout/ppr-item-layout";
import PprChipStatus, {
  ChipStatus,
  CustomStatus,
} from "../../../shared/ppr-chip-status/ppr-chip-status";
import UrlErrorDetail from "./url-error-detail";
import { checkURLs } from "./util/network-url-check";

export type NetworkCheckUploadOptions = {
  url: string;
  minSpeed: number;
  timeout: number;
};

export type NetworkCheckDownloadOptions = NetworkCheckUploadOptions & {
  fileSize: number;
};

export type NetworkCheckInfo = {
  download?: NetworkCheckDownloadOptions;
  upload?: NetworkCheckUploadOptions;
};

interface NetworkSpeedData {
  key: string;
  modality?: string;
  speedType: string;
  requiredSpeed: string;
  yourResult: ChipStatus;
}

interface NetworkCheckProps {
  onComplete: () => void;
  onFail: (
    errorList: ReactNode[],
    title?: string,
    showFAQLink?: boolean,
    canRetry?: boolean,
  ) => void;
  options?: NetworkCheckInfo;
  urlCheck: boolean;
  networkDownloadCheck: boolean;
  networkUploadCheck: boolean;
  retry: number;
}

function NetworkCheck({
  onComplete,
  onFail,
  options,
  urlCheck,
  networkDownloadCheck,
  networkUploadCheck,
  retry,
}: NetworkCheckProps) {
  const { t } = useTranslation();

  const downloadCheckOptions = options?.download || DEFAULT_DOWNLOAD_CHECK;
  const uploadCheckOptions = options?.upload || DEFAULT_UPLOAD_CHECK;

  const networkTableColumns: ColumnsType<NetworkSpeedData> = [
    {
      title: t("NETWORK_CHECK.TABLE_MODALITY"),
      dataIndex: "modality",
      key: "modality",
      onCell: (_, index) => {
        if (index === 0) {
          return { rowSpan: 2 };
        }
        return { rowSpan: 0 };
      },
      render: () => {
        return t("NETWORK_CHECK.TABLE_MODALITY_VALUE");
      },
    },
    {
      title: t("NETWORK_CHECK.TABLE_SPEED_TYPE"),
      dataIndex: "speedType",
      key: "speedType",
    },
    {
      title: t("NETWORK_CHECK.TABLE_REQUIRED_SPEED"),
      dataIndex: "requiredSpeed",
      key: "requiredSpeed",
    },
    {
      title: t("NETWORK_CHECK.TABLE_YOUR_RESULT"),
      dataIndex: "yourResult",
      key: "yourResult",
      render: (value) => {
        let status: ChipStatus | CustomStatus = "meet-requirement";
        if (value === "failed") status = "not-meet-requirement";
        return <PprChipStatus status={status} />;
      },
    },
  ];

  const [downloadSpeedStatus, setDownloadSpeedStatus] =
    useState<ChipStatus>("not-started");
  const [uploadSpeedStatus, setUploadSpeedStatus] =
    useState<ChipStatus>("not-started");
  const [urlCheckStatus, setUrlCheckStatus] =
    useState<ChipStatus>("not-started");

  const [urlCheckErrorList, setUrlCheckErrorList] = useState<string[]>([]);
  const [downloadSpeed, setDownloadSpeed] = useState<number>(0);
  const [uploadSpeed, setUploadSpeed] = useState<number>(0);

  const [shouldShowResultTable, setShouldShowResultTable] = useState(false);
  const [isCheckDone, setIsCheckDone] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout>();

  const dataSources = useCallback(() => {
    let initData: NetworkSpeedData[] = [
      {
        key: "download",
        speedType: t("NETWORK_CHECK.TABLE_DOWNLOAD_SPEED"),
        requiredSpeed: `${downloadCheckOptions.minSpeed} Mbps`,
        yourResult: downloadSpeedStatus,
      },
      {
        key: "upload",
        speedType: t("NETWORK_CHECK.TABLE_UPLOAD_SPEED"),
        requiredSpeed: `${uploadCheckOptions.minSpeed} Mbps`,
        yourResult: uploadSpeedStatus,
      },
    ];

    if (!networkDownloadCheck) {
      initData = initData.filter((item) => item.key !== "download");
    }

    if (!networkUploadCheck) {
      initData = initData.filter((item) => item.key !== "upload");
    }

    return initData;
  }, [
    downloadSpeedStatus,
    uploadSpeedStatus,
    networkDownloadCheck,
    networkUploadCheck,
  ]);

  const checkNetwork = async (
    url: boolean = true,
    download: boolean = true,
    upload: boolean = true,
  ) => {
    setIsCheckDone(false);

    if (url) {
      setUrlCheckStatus("analyzing");
      setUrlCheckErrorList([]);
      const { urlCheckStatus, urlErrorList } = await checkURLs();
      setUrlCheckStatus(urlCheckStatus);
      setUrlCheckErrorList(urlErrorList);
    }

    if (download) {
      setDownloadSpeedStatus("analyzing");
      const { downloadSpeed, downloadStatus } =
        await checkDownloadSpeed(downloadCheckOptions);
      setDownloadSpeed(downloadSpeed);
      setDownloadSpeedStatus(downloadStatus);
    }

    if (upload) {
      setUploadSpeedStatus("analyzing");
      const { uploadSpeed, uploadStatus } =
        await checkUploadSpeed(uploadCheckOptions);
      setUploadSpeed(uploadSpeed);
      setUploadSpeedStatus(uploadStatus);
    }

    setIsCheckDone(true);
  };

  useEffect(() => {
    setDownloadSpeedStatus("not-started");
    setUploadSpeedStatus("not-started");
    setUrlCheckStatus("not-started");
    setShouldShowResultTable(false);

    timeoutRef.current = setTimeout(() => {
      checkNetwork(urlCheck, networkDownloadCheck, networkUploadCheck);
    }, 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [retry]);

  useEffect(() => {
    if (!isCheckDone) {
      return;
    }

    let errorArray = [];
    let showFAQLink = false;
    let failForNetwork = false;

    if (
      (!networkUploadCheck || uploadSpeedStatus === "passed") &&
      (!networkDownloadCheck || downloadSpeedStatus === "passed") &&
      (!urlCheck || urlCheckStatus === "passed")
    ) {
      onComplete();
    }

    if (urlCheckStatus === "failed")
      errorArray.push(<UrlErrorDetail urlErrorList={urlCheckErrorList} />);
    if (uploadSpeedStatus === "failed" || downloadSpeedStatus === "failed") {
      failForNetwork = true;
    }

    if (errorArray.length > 0 || failForNetwork)
      onFail(errorArray, "", showFAQLink, true);

    if (networkDownloadCheck || networkUploadCheck) {
      setShouldShowResultTable(true);
    }
  }, [isCheckDone]);

  const getStatusDescription = (
    status: ChipStatus,
    speedValue: number,
    isUrlCheck?: boolean,
  ) => {
    const statusMapping = {
      "not-started": t("NETWORK_CHECK.STATUS_NOT_CHECKED"),
      "analyzing": t("NETWORK_CHECK.STATUS_IN_PROGRESS"),
      "failed": isUrlCheck ? t("NETWORK_CHECK.URL_CHECK_FAIL") : t("NETWORK_CHECK.STATUS_YOUR_SPEED", { speedValue }),
      "passed": isUrlCheck ? t("NETWORK_CHECK.URL_CHECK_PASS") : t("NETWORK_CHECK.STATUS_YOUR_SPEED", { speedValue }),
    }

    return statusMapping[status] ?? t("NETWORK_CHECK.STATUS_NOT_CHECKED");
  };

  return (
    <>
      <Flex className="flex-layout" vertical gap={45}>
        {urlCheck && (
          <PprItemLayout
            title={t("NETWORK_CHECK.URL_CHECK")}
            desc={getStatusDescription(urlCheckStatus, 0, urlCheck)}
            child={<PprChipStatus status={urlCheckStatus} />}
          />
        )}
        {networkDownloadCheck && (
          <PprItemLayout
            title={t("NETWORK_CHECK.TABLE_DOWNLOAD_SPEED")}
            desc={getStatusDescription(downloadSpeedStatus, downloadSpeed)}
            child={<PprChipStatus status={downloadSpeedStatus} />}
          />
        )}

        {networkUploadCheck && (
          <PprItemLayout
            title={t("NETWORK_CHECK.TABLE_UPLOAD_SPEED")}
            desc={getStatusDescription(uploadSpeedStatus, uploadSpeed)}
            child={<PprChipStatus status={uploadSpeedStatus} />}
          />
        )}
      </Flex>

      {shouldShowResultTable && (
        <Table
          className="network-check-result-table"
          columns={networkTableColumns}
          dataSource={dataSources()}
          pagination={false}
          rowHoverable={false}
          bordered
        />
      )}
    </>
  );
}

export default NetworkCheck;
